import ErrorPage from '@/ErrorPage';
import { access } from '@/access';
import LoadingSpinner from '@/components/LoadingSpinner';
import type { RouteHandle } from '@/guards/routeAccess';
import type { ComponentType } from 'react';
import { Outlet, replace, type RouteObject } from 'react-router-dom';
import BaseRoute from './BaseRoute';
import NotFoundPage from './NotFoundPage';
import Home from './pages/home/Home';
import ArchivedPolicies from './pages/governance/CustomPolicy/ArchivedPolicies';
import EditPolicy from './pages/governance/CustomPolicy/EditPolicy';
import NewPolicy from './pages/governance/CustomPolicy/NewPolicy';
import Policies from './pages/governance/CustomPolicy/Policies';
import PolicyTemplateLibrary from './pages/governance/CustomPolicy/PolicyTemplateLibrary';

/**
 * Index route that redirects a section's bare path (e.g. `/report`) to its
 * default page (e.g. `/report/overview`), preserving any query string.
 * Redirects in the loader, so no component mounts; declared next to the section
 * it belongs to instead of as an imperative check in `BaseRoute`.
 */
const sectionIndexRedirect = (
  to: string,
  handle: RouteHandle,
): DashboardRoute => ({
  index: true,
  loader: ({ request }) => {
    const { search } = new URL(request.url);
    return replace(`${to}${search}`);
  },
  handle,
});

/**
 * A route must declare a `handle`, so its access policy is always visible at the
 * declaration site rather than silently inherited from an ancestor.
 */
type DashboardRoute = RouteObject & {
  handle: RouteHandle;
  children?: DashboardRoute[];
};

/**
 * Identity wrapper whose `const` type parameter keeps `path` values as string
 * literals instead of widening them to `string`, so `RoutePath` can be derived
 * from the tree below.
 */
const defineRoutes = <const T extends readonly DashboardRoute[]>(r: T) => r;

type PathsOf<T> = T extends readonly unknown[]
  ? PathsOf<T[number]>
  :
      | (T extends { path: infer P extends string } ? P : never)
      | (T extends { children: infer C } ? PathsOf<C> : never);

const BaseRouteComponent = BaseRoute as unknown as ComponentType;

const routeTree = defineRoutes([
  {
    handle: {
      access: access.public,
    },
    path: '/',
    element: <BaseRouteComponent />,
    errorElement: <ErrorPage />,
    hydrateFallbackElement: <LoadingSpinner />,
    children: [
      {
        path: '/',
        index: true,
        handle: {
          access: access.user,
        },
        element: <Home />,
      },
      {
        path: 'privacy-policy',
        handle: {
          access: access.public,
        },
        lazy: () => import('@pages/PrivacyPolicy.jsx'),
      },
      {
        path: 'demo',
        handle: {
          access: access.public,
        },
        lazy: () => import('@pages/Demo.jsx'),
      },
      {
        path: 'authentication/login',
        lazy: () => import('./pages/authentication/Login.tsx'),
        handle: {
          access: access.guest,
        },
      },
      {
        path: 'authentication/oidc-callback',
        lazy: () => import('./pages/authentication/OIDCCallback.tsx'),
        handle: {
          access: access.public,
        },
      },
      {
        path: 'authentication/change-password',
        lazy: () => import('./pages/authentication/ChangePassword.tsx'),
        handle: {
          access: access.public,
        },
      },
      {
        path: 'authentication/loginAsCustomer',
        lazy: () => import('./pages/authentication/LoginAsCustomer.jsx'),
        handle: {
          access: access.public,
        },
      },
      {
        path: '/organisation',
        lazy: () => import('./pages/organisation/Organisation.tsx'),
        handle: {
          access: access.organisation,
        },
      },
      {
        path: '/organisation/teams/:teamId',
        lazy: () => import('./pages/organisation/teams/TeamDetailsPage.tsx'),
        handle: {
          access: access.organisation,
        },
      },
      {
        path: '/partner',
        handle: {
          access: access.partner,
        },
        children: [
          {
            index: true,
            path: '/partner/marketing',
            lazy: () => import('./pages/partner/Marketing.jsx'),
            handle: {
              access: access.partner,
            },
          },
          {
            index: true,
            path: '/partner/licenses',
            lazy: () => import('./pages/partner/LicenseOverview.jsx'),
            handle: {
              access: access.partner,
            },
          },
        ],
      },
      {
        path: '/assist',
        element: <Outlet />,
        handle: {
          access: access.user,
        },
        children: [
          sectionIndexRedirect('/assist/overview', { access: access.user }),
          {
            path: '/assist/overview',
            lazy: () => import('@pages/assist/Assist.tsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/assist/customize',
            lazy: () => import('@/pages/assist/customize/AssistCustomize.tsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/assist/installation',
            lazy: () => import('@pages/assist/Installation'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/assist/data-privacy',
            lazy: () => import('@pages/assist/DataPrivacy.jsx'),
            handle: {
              access: access.user,
            },
          },
        ],
      },
      {
        element: <Outlet />,
        path: '/plain-language',
        handle: {
          access: access.user,
        },
        children: [
          {
            index: true,
            path: '/plain-language/simplify',
            lazy: () => import('./pages/plainLanguage/PlainLanguageText.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/plain-language/document',
            lazy: () => import('./pages/plainLanguage/PlainLanguageDocument'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/plain-language/websites',
            element: <Outlet />,
            handle: {
              access: access.plainLanguage,
            },
            children: [
              {
                index: true,
                path: '/plain-language/websites',
                lazy: () => import('./pages/plainLanguage/Overview.jsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                index: true,
                path: '/plain-language/websites/languages',
                lazy: () => import('./pages/plainLanguage/EditorLanguages.jsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: '/plain-language/websites/translation/:domainId/:translPair',
                lazy: () =>
                  import('./pages/plainLanguage/EditorTranslations.jsx'),
                handle: {
                  access: access.user,
                },
              },
            ],
          },
          {
            path: '/plain-language/configurator',
            lazy: () =>
              import('@/pages/plainLanguage/PlainLanguageConfigurator.jsx'),
            handle: {
              access: access.plainLanguage,
            },
          },
          {
            path: '/plain-language/installation',
            lazy: () => import('./pages/plainLanguage/Installation'),
            handle: {
              access: access.plainLanguage,
            },
          },
          {
            path: '/plain-language/dataprivacy',
            lazy: () => import('./pages/plainLanguage/DataPrivacy.jsx'),
            handle: {
              access: access.user,
            },
          },
        ],
      },
      {
        element: <Outlet />,
        path: '/translate',
        handle: {
          access: access.translate,
        },
        children: [
          {
            path: '/translate/websites',
            element: <Outlet />,
            handle: {
              access: access.user,
            },
            children: [
              {
                index: true,
                path: '/translate/websites',
                lazy: () => import('./pages/Translate/Overview.jsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                index: true,
                path: '/translate/websites/languages',
                lazy: () => import('./pages/Translate/EditorLanguages.jsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: '/translate/websites/translation/:domainId/:translPair',
                lazy: () => import('./pages/Translate/EditorTranslations.jsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: '/translate/websites/glossaries/:domainId/:translPair',
                lazy: () => import('./pages/Translate/EditorGlossaries.tsx'),
                handle: {
                  access: access.user,
                },
              },
            ],
          },
          {
            path: '/translate/configurator',
            lazy: () => import('@/pages/Translate/TranslateConfigurator.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/translate/installation',
            lazy: () => import('./pages/Translate/Installation'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/translate/dataprivacy',
            lazy: () => import('./pages/Translate/DataPrivacy.jsx'),
            handle: {
              access: access.user,
            },
          },
        ],
      },
      // --- Audit AREA ---
      {
        handle: {
          access: access.user,
        },
        path: '/audit',
        lazy: () => import('@/pages/audit/Audit.tsx'),
      },
      {
        handle: {
          access: access.user,
        },
        path: '/policies',
        element: <Policies />,
      },
      {
        handle: {
          access: access.user,
        },
        path: '/policies/new',
        element: <NewPolicy />,
      },
      {
        handle: {
          access: access.user,
        },
        path: '/policies/:policyId/edit',
        element: <EditPolicy />,
      },
      {
        handle: {
          access: access.user,
        },
        path: '/policies/templates',
        element: <PolicyTemplateLibrary />,
      },
      {
        handle: {
          access: access.user,
        },
        path: '/policies/archived',
        element: <ArchivedPolicies />,
      },
      {
        path: '/rulesOverview',
        element: <Outlet />,
        handle: {
          access: access.user,
        },
        children: [
          {
            index: true,
            lazy: () => import('@pages/rules/RulesOverview.tsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: ':ruleId',
            lazy: () => import('@pages/rules/RuleDetail'),
            handle: {
              i18nNameKey: 'ruleDetail',
              access: access.user,
            },
          },
        ],
      },
      {
        path: '/report',
        element: <Outlet />,
        handle: {
          access: access.user,
        },
        children: [
          sectionIndexRedirect('/report/overview', { access: access.user }),
          {
            path: '/report/overview',
            lazy: () =>
              import('./pages/report/AccessibilityOverview/AccessibilityOverview.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/issues',
            element: <Outlet />,
            handle: {
              access: access.user,
            },
            children: [
              {
                index: true,
                path: '/report/issues',
                lazy: () => import('./pages/report/Issues/Issues.tsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: 'issue/:ruleId',
                lazy: () => import('./pages/report/IssueDetail.jsx'),
                handle: {
                  access: access.user,
                },
              },
            ],
          },
          {
            path: '/report/pages',
            element: <Outlet />,
            handle: {
              access: access.user,
            },
            children: [
              {
                path: '/report/pages',
                index: true,
                lazy: () => import('./pages/report/pages/ReportPages.tsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: '/report/pages/page/:pageIndex',
                lazy: () => import('./pages/report/pages/PageDetails.tsx'),
                handle: {
                  access: access.user,
                },
              },
            ],
          },
          {
            path: '/report/report-export',
            element: <Outlet />,
            handle: {
              access: access.user,
            },
            children: [
              {
                path: '/report/report-export',
                index: true,
                lazy: () =>
                  import('./pages/report/report-export/ReportGenerator.tsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: '/report/report-export/files',
                lazy: () =>
                  import('./pages/report/report-export/ReportExportJobs.tsx'),
                handle: {
                  access: access.user,
                },
              },
            ],
          },
          {
            path: '/report/settings',
            lazy: () => import('./pages/report/ReportSettings/ReportSettings'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '*',
            element: <NotFoundPage is404 />,
            handle: {
              skipAreaLoading: true,
            },
          },
        ],
      },
      {
        path: '/report/my-goals',
        lazy: () => import('./pages/report/MyGoals/MyGoals.jsx'),
        handle: {
          access: access.user,
        },
      },
      {
        element: <Outlet />,
        path: '/report/tests',
        handle: {
          access: access.user,
        },
        children: [
          {
            index: true,
            path: '/report/tests',
            lazy: () =>
              import('./pages/report/EyeTest/GuidedTests/Overview.tsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/tests/test/:guidedTestId',
            lazy: () =>
              import('./pages/report/EyeTest/GuidedTests/GuidedTest.jsx'),
            handle: {
              access: access.user,
            },
          },
        ],
      },
      {
        element: <Outlet />,
        path: '/report/pdfs',
        handle: {
          access: access.user,
        },
        children: [
          {
            index: true,
            path: '/report/pdfs/',
            lazy: () => import('./pages/report/PDF/Found.tsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/pdfs/found/pdf/:title',
            lazy: () => import('./pages/report/PDF/details/Details.tsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/pdfs/upload',
            element: <Outlet />,
            handle: {
              access: access.user,
            },
            children: [
              {
                path: '/report/pdfs/upload',
                index: true,
                lazy: () => import('./pages/report/PDF/Upload.tsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: '/report/pdfs/upload/pdf/:title',
                lazy: () => import('./pages/report/PDF/details/Details.tsx'),
                handle: {
                  access: access.user,
                },
              },
            ],
          },
          {
            index: true,
            path: '/report/pdfs/pdfsIssues',
            lazy: () => import('./pages/report/PDF/Issues.tsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/pdfs/improvePDF',
            element: <Outlet />,
            handle: {
              access: access.user,
            },
            children: [
              {
                index: true,
                path: '/report/pdfs/improvePDF',
                lazy: () => import('./pages/report/PDF/Improvements.tsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: '/report/pdfs/improvePDF/upload/pdf/:title',
                lazy: () =>
                  import('./pages/report/PDF/details/ImproveDetails.tsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: '/report/pdfs/improvePDF/found/pdf/:title',
                lazy: () =>
                  import('./pages/report/PDF/details/ImproveDetails.tsx'),
                handle: {
                  access: access.user,
                },
              },
            ],
          },
        ],
      },
      {
        element: <Outlet />,
        path: '/report/analysis',
        handle: {
          access: access.user,
        },
        children: [
          {
            path: '/report/analysis/broken-links',
            lazy: () => import('./pages/report/BrokenLinks.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/analysis/lighthouse',
            element: <Outlet />,
            handle: {
              access: access.user,
            },
            children: [
              {
                index: true,
                path: '/report/analysis/lighthouse',
                lazy: () =>
                  import('@pages/report/Lighthouse/LighthouseOverview.tsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: '/report/analysis/lighthouse/page/:filename',
                lazy: () =>
                  import('@pages/report/Lighthouse/LighthouseDetailPage.tsx'),
                handle: {
                  access: access.user,
                },
              },
            ],
          },
          {
            path: '/report/analysis/spellchecker',
            element: <Outlet />,
            handle: {
              access: access.user,
            },
            children: [
              {
                path: '/report/analysis/spellchecker',
                index: true,
                lazy: () =>
                  import('./pages/report/SpellChecker/SpellChecker.tsx'),
                handle: {
                  access: access.user,
                },
              },
              {
                path: '/report/analysis/spellchecker/page/:pageIndex',
                lazy: () =>
                  import('./pages/report/SpellChecker/SpellCheckerPageDetail.tsx'),
                handle: {
                  access: access.user,
                },
              },
            ],
          },
          {
            path: '/report/analysis/fontOverview/',
            lazy: () => import('./pages/report/fontOverview/fontOverview.jsx'),
            handle: {
              access: access.user,
            },
          },
        ],
      },
      {
        path: '/governance',
        element: <Outlet />,
        handle: {
          access: access.user,
        },
        children: [
          sectionIndexRedirect('/governance/overview', { access: access.user }),
          {
            path: '/governance/overview',
            lazy: () => import('./pages/governance/Overview'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/governance/issues',
            lazy: () => import('./pages/governance/Issues/Issues'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/governance/built-in-checks',
            lazy: () => import('./pages/governance/BuiltInChecks'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/governance/custom-policies',
            element: <Policies />,
            handle: {
              access: access.user,
            },
          },
        ],
      },
      {
        element: <Outlet />,
        path: '/report/accessibility-statement',
        handle: {
          access: access.user,
        },
        children: [
          {
            index: true,
            lazy: () =>
              import('./pages/report/accessibility-statement/GeneratedStatements.tsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/accessibility-statement/generator',
            lazy: () =>
              import('./pages/report/accessibility-statement/Generator.tsx'),
            handle: {
              access: access.user,
            },
          },
        ],
      },
      {
        element: <Outlet />,
        path: '/report/access',
        handle: {
          access: access.accessModule,
        },
        children: [
          {
            path: '/report/access/overview',
            lazy: () => import('@pages/access/Access.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/access/installation',
            lazy: () => import('@pages/access/Installation.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/access/dataprivacy',
            lazy: () => import('@pages/access/DataPrivacy.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/access/settings',
            lazy: () => import('@pages/access/Settings.jsx'),
            handle: {
              access: access.admin,
            },
          },
        ],
      },
      {
        element: <Outlet />,
        path: '/report/manual-tests',
        handle: {
          access: access.user,
        },
        children: [
          {
            index: true,
            path: '/report/manual-tests',
            lazy: () =>
              import('./pages/report/EyeTest/ManualTests/CompletedPage.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/manual-tests/unpublished',
            lazy: () =>
              import('./pages/report/EyeTest/ManualTests/DraftPage.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/manual-tests/start',
            lazy: () =>
              import('./pages/report/EyeTest/ManualTests/StartTest.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/manual-tests/test/:testId',
            lazy: () =>
              import('./pages/report/EyeTest/ManualTests/ManualTest.jsx'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/report/manual-tests/scope/:testId',
            lazy: () =>
              import('./pages/report/EyeTest/ManualTests/ManualTestScope.jsx'),
            handle: {
              access: access.user,
            },
          },
        ],
      },
      {
        path: '/report/compare',
        lazy: () => import('./pages/report/CompareReport/CompareReport.jsx'),
        handle: {
          access: access.user,
        },
      },
      // --- TOOLS ---
      {
        path: '/ally',
        lazy: () => import('./pages/ally/Ally.tsx'),
        handle: {
          access: access.user,
        },
      },
      {
        path: '/transcription',
        element: <Outlet />,
        handle: {
          access: access.user,
        },
        children: [
          {
            path: '/transcription',
            index: true,
            lazy: () => import('@pages/Transcription/Transcription'),
            handle: {
              access: access.user,
            },
          },
          {
            path: '/transcription/:mediaUrl',
            lazy: () => import('@pages/Transcription/Transcription'),
            handle: {
              access: access.user,
            },
          },
        ],
      },
      {
        path: '/subscriptions',
        lazy: () => import('./pages/subscriptions/Subscriptions.jsx'),
        handle: {
          access: access.user,
        },
      },
      {
        path: '/contrast-check',
        lazy: () => import('./pages/ContrastChecker/ContrastChecker.tsx'),
        handle: {
          access: access.user,
        },
      },
      {
        path: '/support-tickets',
        lazy: () => import('./pages/tickets/Tickets.jsx'),
        handle: {
          access: access.supportTickets,
        },
      },
      {
        path: '/i18n',
        lazy: () => import('./pages/i18n/Release.tsx'),
        handle: {
          access: access.public,
        },
      },
      // --- (sales) ADMIN AREA ---
      {
        path: '/admin/panel',
        index: true,
        lazy: () => import('./pages/admin/AdminPanel.jsx'),
        handle: {
          access: access.salesAdmin,
        },
      },
      {
        path: '/admin/crawler-status',
        index: true,
        lazy: () => import('./pages/admin/CrawlerStatus.jsx'),
        handle: {
          access: access.admin,
        },
      },
      {
        path: '/help-center',
        lazy: () => import('./pages/helpCenter/HelpCenter'),
        handle: {
          access: access.user,
        },
      },
      {
        path: '/not-found',
        element: <NotFoundPage />,
        handle: {
          access: access.public,
        },
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage is404 />,
    handle: {
      access: access.public,
    },
  },
]);

/**
 * Every absolute path declared in the route tree. Navigation entries are typed
 * against this, so a sidebar item can never point at a route that no longer
 * exists.
 */
export type RoutePath = Extract<PathsOf<typeof routeTree>, `/${string}`>;

export default routeTree as unknown as DashboardRoute[];
